import CountriesDropdown from 'components/Common/countries-dropdown';
import React, { useEffect, useState } from 'react';
import { benchmarkingAnalytics } from 'services/benchmarkingServices';
import SystemsDropdown from './SystemsDropdown';
import BenchmarkingBarChart from './BarChart';
import downloadChart from 'components/Common/ImageToPdf';
import { Icon } from '@iconify/react';
import LoadingSpinner from 'components/Common/Loading-Spinner';
import { usePublic } from 'helpers/utlities';

function Visualisation({ alert }) {
    const [tab, setTab] = useState('system');
    const [countryID, setCountryID] = useState();
    const [systemID, setSystemID] = useState();
    const [data, setData] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [showExplainer, setShowExplainer] = useState(false);

    const getData = async () => {
        try{
            setIsLoading(true);
            let query = {
                view_type: tab,
                ...(countryID && { country_id: countryID }),
                ...(systemID && { system_id: systemID })
            };
            setData(await benchmarkingAnalytics(query));
        } catch (err) {
            setData();
            console.error(err);
            alert('Fetching data', false);
        }finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getData();
    }, [tab, systemID, countryID])

    const BenchmarkingExplainer = () => (
        <div className="bg-light p-3 mb-6 shadow-sm">
            <h2 className="flex items-center text-xl font-medium text-green-700 mb-3">
                {/* <span className="mr-2">📊</span> */}
                Policy Benchmarking Explainer
            </h2>

            <p className="text-gray-600 mb-2">
                Policy benchmarking evaluates the adequacy of policies by measuring their coverage, implementation, and effectiveness.
                The final score is a weighted combination of these three factors, providing insights into policy 
                performance across various agri-food systems and countries.
            </p>
            
            <div className="mb-3">
                <h3 className="flex items-center text-base font-medium mb-1">
                    <span className="mr-2">📈</span>
                    Visualization
                </h3>
                <p className="text-gray-600 mb-1">
                    Policy benchmarking visualizations consist of two main sections: <span className='font-medium'>System</span> and <span className='font-medium'>Country</span>.
                </p>
                <div className="text-gray-600">
                    <div className="mb-1">
                        <span className='mr-2'>🌾</span><span className="font-medium mr-1">Systems:</span>Displays benchmarking scores for all agri-food systems (or the selected system) across AGRA-focus countries. Each score appears alongside regional averages.
                    </div>
                    <span className='mr-2'>🌍</span><span className="font-medium mr-1">Country:</span>Provides agri-food system scores per country, alongside regional and AGRA-focus country averages for comparison.
                </div>
            </div>
            
            <div className="flex justify-end">
                <button 
                    onClick={() => setShowExplainer(false)}
                    className="text-sm text-gray-600 hover:text-gray-800 flex items-center"
                >
                    <span>Hide explainer</span>
                    <Icon icon="mynaui:chevron-up" width="20" height="20" className='ml-1'/>
                </button>
            </div>
        </div>
    );

    return (
        <div>
            <div className="flex justify-between mb-4">
                <div className="country-selection">
                    <span className="mr-2 text-nowrap">Showing data for </span>
                    {
                        tab === 'system'
                            ?
                            <SystemsDropdown systemID={systemID} setSystemID={setSystemID} />
                            :
                            <CountriesDropdown countryID={countryID} setCountryID={setCountryID} hasRAC={true} />
                    }
                </div>
                <button 
                    className="common-button" 
                    disabled={isLoading || isDownloading} 
                    title="Download PDF"
                    onClick={()=>downloadChart('benchmark-chart', setIsDownloading, `benchmark-chart-${tab}-${(data?.system_name || data?.long_name) ? (data?.system_name || data?.long_name): 'All'}-${new Date().toISOString().split('T')[0]}.pdf`)}>
                    {isDownloading ? <Icon icon="codex:loader"/> : <i className="bx bx-download"/>}
                </button>
            </div>
            
            <div className='flex justify-center mb-4 gap-2'>
                <div className={`sub-tab ${tab === 'system' ? 'sub-tab-active' : ''}`} onClick={()=>setTab('system')}>System</div>
                <div className={`sub-tab ${tab === 'country' ? 'sub-tab-active' : ''}`} onClick={()=> setTab('country')}>Country</div>
            </div>

            {usePublic() && (
                showExplainer ? (
                    <BenchmarkingExplainer />
                ) : (
                    <div className="flex justify-end px-3 pb-1 pt-0">
                        <button 
                            onClick={() => setShowExplainer(true)}
                            className="flex items-center text-sm font-medium text-green-600 hover:text-green-800"
                        >
                            <Icon icon="material-symbols:info-outline-rounded" width="20" height="20" className='mr-1'/>
                            Show policy benchmarking explainer
                        </button>
                    </div>
                )
            )}
            
            <div id="benchmark-chart">
                {isLoading ? (
                    <LoadingSpinner/>
                ) : data ? (
                    <BenchmarkingBarChart data={data?.data || data} tab={tab} filterSelected={data?.system_name || data?.long_name} dottedLineValue={data?.all_countries_system_average} region={data?.region}/>
                ) : <p>Unable to fetch data.</p>}
            </div>
        </div>
    );
}

export default Visualisation;